import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

const PUBLIC_AUTH_PATHS = [
  '/autenticacao/refresh',
  '/autenticacao/login',
  '/autenticacao/callback',
] as const;

/** Logout: AuthService anexa Bearer explicitamente (evita refresh automático). */
function isLogoutRequest(url: string): boolean {
  return url.includes('/autenticacao/logout');
}

function resolveApiOrigin(): string | null {
  try {
    return new URL(environment.apiUrl).origin;
  } catch {
    return null;
  }
}

/** Só anexa Bearer a requests da API SGI (deny-by-default para outras origens). */
function isSgiApiRequest(url: string): boolean {
  const apiOrigin = resolveApiOrigin();
  if (!apiOrigin) {
    return url.startsWith(environment.apiUrl);
  }

  try {
    const absolute = url.startsWith('http://') || url.startsWith('https://')
      ? url
      : new URL(url, typeof window !== 'undefined' ? window.location.origin : apiOrigin).href;
    return new URL(absolute).origin === apiOrigin;
  } catch {
    return false;
  }
}

function isPublicAuthRequest(url: string): boolean {
  return PUBLIC_AUTH_PATHS.some((path) => url.includes(path));
}

function sendWithBearer(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  token: string,
): ReturnType<HttpHandlerFn> {
  const authReq = req.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && authService.getRefreshToken() && !authService.isSessionInvalidated()) {
        return authService.refreshAccessToken().pipe(
          switchMap((newToken) => sendWithBearer(req, next, authService, newToken)),
          catchError((refreshErr) => {
            authService.signOutLocally();
            return throwError(() => refreshErr);
          }),
        );
      }

      return throwError(() => error);
    }),
  );
}

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const authService = inject(AuthService);

  if (!isSgiApiRequest(req.url) || isPublicAuthRequest(req.url) || isLogoutRequest(req.url)) {
    return next(req);
  }

  const accessToken = authService.getAccessToken();
  if (accessToken) {
    return sendWithBearer(req, next, authService, accessToken);
  }

  const refreshToken = authService.getRefreshToken();
  if (!refreshToken || authService.isSessionInvalidated()) {
    return next(req);
  }

  return authService.refreshAccessToken().pipe(
    switchMap((newToken) => sendWithBearer(req, next, authService, newToken)),
    catchError((refreshErr) => {
      authService.signOutLocally();
      return throwError(() => refreshErr);
    }),
  );
};
